import { useMutation, useQuery } from "@apollo/client";
import { ChangeEvent } from "react";
import { Link } from "react-router-dom";
import { toast } from "react-toastify";
import { SET_ACTIVE_TIP_MENUS } from "../../../queries/mutation/tip_menu";
import { GET_TIP_MENUS, GET_TIP_MENU_OVERLAPS } from "../../../queries/tip_menu";
import { GET_USER_INFO } from "../../../queries/user_queries";
import { TipMenu, TipMenuOverlap } from "../../../types/TipMenu";
import User from "../../../types/User";
import Loading from "../../Loading";
import DashCard from "./DashCard";

// Picks which menus are live: a main one and an optional second. The main
// one wins when both have an item at the same token amount.
const SelectedTipMenuWidget = () => {
  const { loading: menusLoading, data: menuData } = useQuery(GET_TIP_MENUS);
  const { data: userData, refetch: refetchUserInfo } = useQuery(GET_USER_INFO);
  const { data: overlapData, refetch: refetchOverlaps } = useQuery(
    GET_TIP_MENU_OVERLAPS,
  );

  const [setActiveTipMenus, { loading: updateLoading }] =
    useMutation(SET_ACTIVE_TIP_MENUS);

  const overlaps: TipMenuOverlap[] = overlapData?.getTipMenuOverlaps ?? [];

  const save = async (ids: string[]) => {
    try {
      await setActiveTipMenus({ variables: { tipMenus: ids } });
      await Promise.all([refetchUserInfo(), refetchOverlaps()]);
    } catch (error) {
      toast.error(
        error instanceof Error ? error.message : "Couldn't update active menus",
      );
    }
  };

  let content;
  if (
    userData?.getUserInfo === undefined ||
    menuData?.getTipMenus === undefined
  ) {
    content = <Loading />;
  } else {
    const user = userData.getUserInfo as User;
    const menus: TipMenu[] = menuData.getTipMenus;
    const [first = "", second = ""] = user.activeMenus ?? [];

    const onChangeFirst = (e: ChangeEvent<HTMLSelectElement>) => {
      const id = e.target.value;
      save([id, second].filter(Boolean));
    };
    const onChangeSecond = (e: ChangeEvent<HTMLSelectElement>) => {
      const id = e.target.value;
      save([first, id].filter(Boolean));
    };

    content = (
      <div className="flex flex-col gap-3">
        <label className="form-control">
          <span className="label-text text-sm">Main menu</span>
          <select
            className="select select-bordered select-primary"
            value={first}
            onChange={onChangeFirst}
            disabled={updateLoading || menusLoading}
          >
            <option value="">None</option>
            {menus
              .filter((m) => m.id !== second)
              .map((m) => (
                <option key={m.id} value={m.id}>
                  {m.name}
                </option>
              ))}
          </select>
        </label>
        <label className="form-control">
          <span className="label-text text-sm">Second menu (optional)</span>
          <select
            className="select select-bordered"
            value={second}
            onChange={onChangeSecond}
            disabled={updateLoading || menusLoading || !first}
          >
            <option value="">None</option>
            {menus
              .filter((m) => m.id !== first)
              .map((m) => (
                <option key={m.id} value={m.id}>
                  {m.name}
                </option>
              ))}
          </select>
        </label>
        {overlaps.length > 0 && (
          <div className="alert alert-warning text-sm py-2">
            <span>
              Both menus have items at {overlaps.map((o) => o.amount).join(", ")}{" "}
              tokens; the main menu's win.{" "}
              <Link to="/tip-menu-editor" className="link">
                Details
              </Link>
            </span>
          </div>
        )}
      </div>
    );
  }

  return (
    <DashCard
      title="Active tip menus"
      aside={
        <Link to="/tip-menu-editor" className="link link-primary text-xs">
          Edit menus
        </Link>
      }
    >
      {content}
    </DashCard>
  );
};

export default SelectedTipMenuWidget;
